import { FirstGameStep, Game, GameManual } from '../types';
import { arkNovaCoreChapters } from './ark-nova/content/core';
import {
  ARK_NOVA_MARINE_CHAPTER_TITLE,
  arkNovaMarineChapters,
  isArkNovaMarineLegacyTitle,
} from './ark-nova/content/marine-worlds';
import { MARINE_WORLDS_EXPANSION_ID } from './ark-nova/marine-worlds/expansion';

export const arkNovaGame: Game = {
  id: 'ark-nova',
  title: 'Ark Nova',
  subtitle: 'Podstawa + Zwierzęta Wodne',
  icon: '🦁',
  players: '1–4 graczy',
  playTime: '90–150 min',
  // The unread printed bonus on reputation space 5 means the base game cannot
  // honestly be marked ready yet. The verified rules remain available.
  contentStatus: 'partial',
  logoAccessibilityLabel: 'Logo gry Ark Nova',
};

export const arkNovaBaseFirstGameSteps: FirstGameStep[] = [
  {
    id: 1,
    title: 'Liczba graczy i mapa A',
    content: [
      'Wybierzcie 2–3 graczy na pierwszą partię (dopuszczalne 1–4).',
      'Użyjcie identycznych map A.',
      'Ustawcie znacznik przerwy na polu startowym dla waszej liczby graczy.',
    ],
    why: 'Mapa A i prosty start ograniczają liczbę wyjątków w pierwszej partii.',
    commonMistake: 'Start na mapie asymetrycznej.',
    chapterIndex: 2,
  },
  {
    id: 2,
    title: 'Wspólny stół',
    content: [
      'Rozłóżcie planszę główną i stowarzyszenie.',
      'Wylosujcie żetony premii i projekty ochrony.',
      'Przygotujcie zakrytą wystawkę (odkryjecie ją na końcu przygotowania).',
    ],
    why: 'Wspólny obszar wyznacza wystawkę, przerwę i ochronę przyrody.',
    commonMistake: 'Odkrycie wystawki za wcześnie.',
    chapterIndex: 2,
  },
  {
    id: 3,
    title: 'Przygotowanie gracza',
    content: [
      'Ustaw znaczniki atrakcyjności, ochrony i reputacji.',
      'Połóż pracowników i pięć kart akcji (Zwierzęta na pozycji 1).',
      'Dobierz 8 kart zoo → zostaw 4; dobierz 2 karty punktacji.',
      'Umieść startowy wybieg wielkości 3.',
    ],
    why: 'Kompletne przygotowanie zapobiega błędom w pierwszej turze i przerwie.',
    commonMistake: 'Brak startowego wybiegu.',
    chapterIndex: 2,
  },
  {
    id: 4,
    title: 'Pierwsza tura',
    content: [
      'Wykonaj dokładnie jedną akcję.',
      'Ustal siłę z pozycji, opcjonalnie wydaj X przed efektem.',
      'Przenieś kartę na pozycję 1.',
      'Sprawdź przerwę i koniec gry.',
    ],
    why: 'Rytm jednej akcji na turę jest podstawą Ark Nova.',
    commonMistake: 'Wykonywanie kilku akcji w jednej turze.',
    chapterIndex: 3,
  },
  {
    id: 5,
    title: 'Bezpieczny początek',
    content: [
      'Zbuduj tani wybieg pod zwierzę z ręki.',
      'Zagraj zwierzę dające atrakcyjność.',
      'Użyj Stowarzyszenia po ogród lub uczelnię.',
      'Wesprzyj co najmniej jeden projekt ochrony w pierwszej połowie gry.',
    ],
    why: 'Prosty schemat uczy obu torów wyniku bez narzucania jedynej strategii.',
    commonMistake: 'Rozwijanie tylko atrakcyjności.',
    chapterIndex: 16,
  },
];

export const arkNovaMarineFirstGameSteps: FirstGameStep[] = [
  {
    id: 6,
    title: 'Draft alternatywnych kart',
    content: [
      'Każdy wybiera dokładnie dwie alternatywne karty akcji.',
      'Zastąp nimi dwa typy podstawowe — nie dwa warianty tego samego typu.',
      'Przeczytaj tylko swoje dwie karty przed startem.',
    ],
    why: 'Draft jest częścią przygotowania Zwierząt Wodnych.',
    commonMistake: 'Dwie alternatywne karty tego samego typu.',
    // Points at the single Marine Worlds chapter (last chapter when expansion on).
    chapterIndex: arkNovaCoreChapters.length,
    requiredAddons: [MARINE_WORLDS_EXPANSION_ID],
  },
  {
    id: 7,
    title: 'Akwaria i Rafa',
    content: [
      'Pokażcie małe i duże akwarium oraz pojemność.',
      'Wyjaśnijcie ikonę zwierzęcia morskiego i Rafę.',
      'Przy pierwszej karcie morskiej przejdźcie checklistę zagrania.',
    ],
    why: 'Te elementy pojawiają się wcześnie i generują najwięcej błędów.',
    commonMistake: 'Zwierzę morskie w standardowym wybiegu.',
    chapterIndex: arkNovaCoreChapters.length,
    requiredAddons: [MARINE_WORLDS_EXPANSION_ID],
  },
];

export function buildArkNovaManual(marineEnabled: boolean): GameManual {
  const chapters = [
    ...arkNovaCoreChapters,
    ...(marineEnabled ? arkNovaMarineChapters : []),
  ];
  const firstGameSteps = [
    ...arkNovaBaseFirstGameSteps,
    ...(marineEnabled ? arkNovaMarineFirstGameSteps : []),
  ];
  return {
    chapters,
    firstGameSteps,
    addons: [
      {
        id: MARINE_WORLDS_EXPANSION_ID,
        name: 'Zwierzęta Wodne',
        description: 'Marine Worlds — pełny moduł dodatku',
      },
    ],
  };
}

/** Default manual with expansion chapters included for registry. */
export const arkNovaManual: GameManual = buildArkNovaManual(true);

export function findChapterIndexByTitle(
  manual: GameManual,
  title: string
): number {
  const exact = manual.chapters.findIndex((chapter) => chapter.title === title);
  if (exact >= 0) return exact;
  // Legacy ZW:* TOC titles now live as headings inside the Marine Worlds chapter.
  if (isArkNovaMarineLegacyTitle(title)) {
    return manual.chapters.findIndex(
      (chapter) => chapter.title === ARK_NOVA_MARINE_CHAPTER_TITLE
    );
  }
  return -1;
}
